import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Mail } from "lucide-react";

export const AUD_STATUS = {
  requested: { label: "Richiesta", cls: "bg-amber-500 text-white", soft: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200" },
  confirmed: { label: "Confermata", cls: "bg-emerald-600 text-white", soft: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200" },
  cancelled: { label: "Annullata", cls: "bg-stone-400 text-white line-through", soft: "bg-stone-200 text-stone-600 dark:bg-stone-800 dark:text-stone-300" },
};
export const AUD_PAY = { unpaid: "Da pagare", deposit: "Acconto", paid: "Pagato" };

const EMPTY = { title: "", date: "", all_day: true, start_time: "09:00", end_time: "13:00", requester_name: "", phone: "", email: "", participants: "", amount: "", payment_status: "unpaid", status: "requested", notes: "" };

const Field = ({ label, children }) => <div><Label>{label}</Label><div className="mt-1.5">{children}</div></div>;

export default function AuditoriumDialog({ open, onOpenChange, booking, defaultDate, defaultTime, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const resend = async () => {
    setSending(true);
    try { await api.post(`/auditorium/${booking.id}/send-confirmation`); toast.success(`Email inviata a ${booking.email}`); onSaved && onSaved(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setSending(false); }
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    if (booking) setForm({ ...EMPTY, ...booking, participants: booking.participants ?? "", amount: booking.amount ?? "", start_time: booking.all_day ? "09:00" : booking.start_time, end_time: booking.all_day ? "13:00" : booking.end_time });
    else setForm({ ...EMPTY, date: defaultDate || "", all_day: !defaultTime, start_time: defaultTime || "09:00", end_time: defaultTime ? `${String(Math.min(23, Number(defaultTime.slice(0, 2)) + 1)).padStart(2, "0")}:00` : "13:00" });
  }, [open, booking, defaultDate, defaultTime]);

  const submit = async () => {
    if (!form.title.trim() || !form.requester_name.trim() || !form.date) { toast.error("Compila evento, richiedente e data"); return; }
    const payload = { ...form, participants: form.participants === "" ? null : Number(form.participants), amount: form.amount === "" ? null : Number(form.amount) };
    setSaving(true);
    try {
      if (booking) await api.put(`/auditorium/${booking.id}`, payload);
      else await api.post("/auditorium", payload);
      toast.success(booking ? "Prenotazione aggiornata" : "Auditorium prenotato");
      onOpenChange(false); onSaved && onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Eliminare questa prenotazione dell'auditorium?")) return;
    try { await api.delete(`/auditorium/${booking.id}`); toast.success("Prenotazione eliminata"); onOpenChange(false); onSaved && onSaved(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="auditorium-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl">{booking ? "Prenotazione auditorium" : "Prenota l'auditorium"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label="Evento / Titolo"><Input data-testid="aud-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Es. Conferenza, Concerto…" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Data"><Input type="date" data-testid="aud-date" value={form.date} onChange={(e) => set("date", e.target.value)} /></Field>
            <Field label="Stato">
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="aud-status"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(AUD_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox data-testid="aud-all-day" checked={form.all_day} onCheckedChange={(v) => set("all_day", !!v)} />Giornata intera
          </label>
          {!form.all_day && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Dalle"><Input type="time" data-testid="aud-start" value={form.start_time} onChange={(e) => set("start_time", e.target.value)} /></Field>
              <Field label="Alle"><Input type="time" data-testid="aud-end" value={form.end_time} onChange={(e) => set("end_time", e.target.value)} /></Field>
            </div>
          )}
          <Field label="Richiedente"><Input data-testid="aud-requester" value={form.requester_name} onChange={(e) => set("requester_name", e.target.value)} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Telefono"><Input data-testid="aud-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
            <Field label="Email"><Input type="email" data-testid="aud-email" value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Partecipanti"><Input type="number" min="0" data-testid="aud-participants" value={form.participants} onChange={(e) => set("participants", e.target.value)} /></Field>
            <Field label="Importo €"><Input type="number" min="0" step="0.01" data-testid="aud-amount" value={form.amount} onChange={(e) => set("amount", e.target.value)} /></Field>
            <Field label="Pagamento">
              <Select value={form.payment_status} onValueChange={(v) => set("payment_status", v)}>
                <SelectTrigger data-testid="aud-payment"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(AUD_PAY).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Note"><Textarea data-testid="aud-notes" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
          {booking && (
            <div className="rounded-md border border-border bg-secondary/40 px-3 py-2 text-xs flex items-center gap-2" data-testid="aud-email-status">
              <Mail className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="flex-1">{booking.confirmation_sent_at ? `Email di conferma inviata il ${new Date(booking.confirmation_sent_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}` : booking.email ? "Alla conferma verrà inviata un'email al richiedente." : "Nessuna email del richiedente: nessuna conferma automatica."}{booking.reminder_sent_at ? ` · Promemoria inviato il ${new Date(booking.reminder_sent_at).toLocaleDateString("it-IT")}` : booking.email && booking.status === "confirmed" ? " · Promemoria automatico il giorno prima." : ""}</span>
              {booking.email && booking.status === "confirmed" && <Button size="sm" variant="outline" data-testid="aud-resend" disabled={sending} onClick={resend} className="h-7 text-xs">{sending ? "Invio…" : "Reinvia"}</Button>}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          {booking ? <Button variant="ghost" data-testid="aud-delete" onClick={remove} className="text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4 mr-1.5" />Elimina</Button> : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Chiudi</Button>
            <Button data-testid="aud-save" onClick={submit} disabled={saving} className="bg-accent hover:bg-accent/90 text-accent-foreground">{booking ? "Salva" : "Prenota"}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
